import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServicesPreview = () => {
  const [hoveredService, setHoveredService] = useState(null);

  const services = [
    {
      id: 'social-media',
      title: 'Social Media Marketing',
      description: 'Strategic social presence that converts followers into customers',
      icon: 'Share2',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      hoverBg: 'hover:bg-accent/20',
      demo: {
        type: 'social-post',
        content: {
          platform: 'Instagram',
          likes: '2.4K',
          comments: '89',
          shares: '156',
          engagement: '12.3%'
        }
      },
      features: ['Content Strategy', 'Community Management', 'Paid Advertising', 'Analytics & Reporting']
    },
    {
      id: 'web-development',
      title: 'Web Development',
      description: 'Responsive, fast, and conversion-optimized websites',
      icon: 'Code',
      color: 'text-trust',
      bgColor: 'bg-trust/10',
      hoverBg: 'hover:bg-trust/20',
      demo: {
        type: 'responsive-layout',
        content: {
          desktop: '1920px',
          tablet: '768px',
          mobile: '375px',
          speed: '98/100'
        }
      },
      features: ['Custom Development', 'E-commerce Solutions', 'CMS Integration', 'Performance Optimization']
    },
    {
      id: 'app-development',
      title: 'App Development',
      description: 'Native and cross-platform mobile applications',
      icon: 'Smartphone',
      color: 'text-conversion',
      bgColor: 'bg-conversion/10',
      hoverBg: 'hover:bg-conversion/20',
      demo: {
        type: 'app-interface',
        content: {
          platform: 'iOS & Android',
          rating: '4.8/5',
          downloads: '50K+',
          retention: '85%'
        }
      },
      features: ['Native Development', 'Cross-Platform Solutions', 'UI/UX Design', 'App Store Optimization']
    }
  ];

  const renderDemo = (demo) => {
    switch (demo?.type) {
      case 'social-post':
        return (
          <div className="bg-white rounded-lg p-4 shadow-md">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
              <span className="font-semibold text-gray-800">@singhcreations</span>
            </div>
            <div className="bg-gray-100 rounded-lg h-32 mb-3 flex items-center justify-center">
              <Icon name="Image" size={32} className="text-gray-400" />
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>{demo?.content?.likes} likes</span>
              <span>{demo?.content?.comments} comments</span>
              <span>{demo?.content?.engagement} engagement</span>
            </div>
          </div>
        );
      case 'responsive-layout':
        return (
          <div className="space-y-3">
            <div className="flex space-x-2">
              <div className="bg-primary h-16 rounded flex-1"></div>
              <div className="bg-primary h-16 w-12 rounded"></div>
              <div className="bg-primary h-16 w-8 rounded"></div>
            </div>
            <div className="text-xs text-center text-gray-600">
              Responsive across all devices • Speed Score: {demo?.content?.speed}
            </div>
          </div>
        );
      case 'app-interface':
        return (
          <div className="bg-gray-900 rounded-xl p-4 text-white">
            <div className="bg-gray-800 rounded-lg h-24 mb-3 flex items-center justify-center">
              <Icon name="Smartphone" size={24} className="text-gray-400" />
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span>Rating:</span>
                <span className="text-yellow-400">{demo?.content?.rating}</span>
              </div>
              <div className="flex justify-between">
                <span>Downloads:</span>
                <span className="text-green-400">{demo?.content?.downloads}</span>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Our Digital Solutions
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            From social media mastery to cutting-edge development, we provide comprehensive digital solutions that drive real business growth.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {services?.map((service) => (
            <div
              key={service?.id}
              className={`bg-card rounded-xl p-8 brand-shadow brand-transition cursor-pointer ${service?.hoverBg} group`}
              onMouseEnter={() => setHoveredService(service?.id)}
              onMouseLeave={() => setHoveredService(null)}
            >
              {/* Service Header */}
              <div className="mb-6">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${service?.bgColor} mb-4 group-hover:scale-110 brand-transition`}>
                  <Icon name={service?.icon} size={32} className={service?.color} />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">
                  {service?.title}
                </h3>
                <p className="text-muted-foreground">
                  {service?.description}
                </p>
              </div>

              {/* Interactive Demo */}
              <div className={`mb-6 transition-all duration-300 ${hoveredService === service?.id ? 'opacity-100 scale-100' : 'opacity-0 scale-95 h-0 overflow-hidden'}`}>
                {hoveredService === service?.id && renderDemo(service?.demo)}
              </div>

              {/* Features List */}
              <div className="space-y-2 mb-6">
                {service?.features?.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className={service?.color} />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <Link to="/services">
                <Button
                  variant="outline"
                  fullWidth
                  className="group-hover:border-primary group-hover:text-primary"
                  iconName="ArrowRight"
                  iconPosition="right"
                  iconSize={16}
                >
                  Learn More
                </Button>
              </Link>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <Link to="/services">
            <Button
              variant="default"
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground brand-hover-lift"
              iconName="Zap"
              iconPosition="left"
              iconSize={20}
            >
              Explore All Services
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;